import { useState, useEffect } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import CircularProgress from '@mui/material/CircularProgress';
import API from '../../api';
import { users as searchByUsers } from '../../helpers/SearchBy';
import { group as locationGroup } from '../../helpers/Location';
import SidePanelContainer from '../SidePanel/SidePanelContainer';
import SidePanelHeader from '../SidePanel/SidePanelHeader';
import SidePanelContent from '../SidePanel/SidePanelContent';
import SidePanelFooter from '../SidePanel/SidePanelFooter';
import PageLayout from '../PageLayout/PageLayout';
import EmptyState from '../EmptyState/EmptyState';
import ApproverCard from '../ApproverCard/ApproverCard';
import VibeButton from '../VibeButton/VibeButton';
import VibeIcon from '../VibeIcon/VibeIcon';
import viChevronLeft from '../../icons/viChevronLeft';
import viUser from '../../icons/viUser';
import * as color from '../../sass/color.scss';
import './ApproverList.scss';

function ApproverList({
  className = '',
  style = {},
  data = {},
  // "partner" or "client"
  approverType,
  // default to "item", currently supports ("message", "flight")
  objectType = 'item',
  onUpdate = () => {},
  onClose = () => {},
}) {
  const approverSettings = get(data, `${approverType}Approval`, {}) || {};
  const approvers = approverSettings.approvers || [];

  const [loading, setLoading] = useState(true);
  const [approversList, setApproversList] = useState([]);
  const [filteredApprovers, setFilteredApprovers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState(sortBy(approvers, 'fname'));

  /**
   * User filters the approver list
   */
  const onFilter = (data) => {
    const {
      collection,
    } = data;

    setFilteredApprovers(collection);
  };

  /**
   * When a user is toggled from the list
   */
  const onSelectApprover = (approver) => {
    const existingApprover = find(approvers, { _id: approver._id }) || {};

    const allApprovers = approversList.map((item) => {
      if (item._id === approver._id) {
        // merge the selected approver with the existing approver data
        return {
          ...existingApprover,
          ...approver,
        };
      }

      // merge the selected approver with the existing approver data
      return {
        ...existingApprover,
        ...item,
      };
    });

    setApproversList(allApprovers);

    setFilteredApprovers(filteredApprovers.map((item) => {
      if (item._id === approver._id) {
        // merge the selected approver with the existing approver data
        return {
          ...existingApprover,
          ...approver,
        };
      }

      // merge the selected approver with the existing approver data
      return {
        ...existingApprover,
        ...item,
      };
    }));

    setSelectedApprovers(sortBy(allApprovers.filter(approver => approver.selected), 'fname').map(approver => {
      return {
        ...approver,
        selected: false,
      };
    }));
  };

  /**
   * User saves the approver selection
   */
  const onAssignApprovers = () => {
    const approverObject = approverType === 'client'
      ? 'clientApproval'
      : 'partnerApproval';

    onUpdate({
      [approverObject]: {
        ...data[approverObject],
        approvers: selectedApprovers,
        // update the required approver count if all approvers are required
        approvalsRequired: approverSettings?.style === 'unanimous'
          ? selectedApprovers.length
          : data[approverObject]?.approvalsRequired ?? 1,
      },
    });

    onClose();
  };

  /**
   * Get the footer style overrides
   * custom footer style needed when displaying selected approvers
   */
  const getFooterStyle = () => {
    const footerStyle = {};

    if (selectedApprovers.length > 0) {
      footerStyle.height = 'auto';
      footerStyle.minHeight = 'auto';
      footerStyle.paddingBottom = 16;
    }

    return footerStyle;
  };

  /**
   * Get the list of approvers
   */
  useEffect(() => {
    async function fetchData() {
      const group = locationGroup(data.locations);
      let response;

      if (objectType === 'message') {
        response = approverType === 'partner'
          ? await API.MessageRequest.Approval.getPartnerApprovers({ partnerId: data.partnerId, locations: group })
          : await API.MessageRequest.Approval.getApprovers({ companyId: data.companyId, locations: group });
      } else if (objectType === 'flight') {
        response = await API.Flight.Approval.getPartnerApprovers({ partnerId: data.partnerId, locations: group });
      }

      setApproversList(response.map((approver) => {
        const selected = find(approvers, { _id: approver._id }) !== undefined;

        return {
          ...approver,
          selected,
        };
      }));

      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <SidePanelContainer
      className={classNames('ApproverList', objectType, className)}
      style={style}
    >
      <SidePanelHeader className="panel-header">
        <VibeIcon
          icon={viChevronLeft}
          color={color.manatee}
          hoverColor={color.obsidian}
          size={24}
          onClick={onClose}
        />

        <PageLayout
          className="page-layout"
          searchOptions={searchByUsers}
          collection={approversList}
          onFilter={onFilter}
          disableView
          disableSort
        />
      </SidePanelHeader>

      <SidePanelContent className="panel-content">
        {loading && (
          <div
            style={{
              textAlign: 'center',
              color: color.primary,
            }}
          >
            <CircularProgress
              color="inherit"
              size={48}
            />
          </div>
        )}

        {filteredApprovers.map((approver, index) => (
          <ApproverCard
            key={`approver-card-${index}`}
            style={{
              padding: 16,
            }}
            approver={approver}
            selectable
            onSelect={onSelectApprover}
          />
        ))}

        {!loading && filteredApprovers.length <= 0 && (
          <EmptyState
            title="No Available Approvers"
            description={`No users found that can approve this ${objectType}`}
            style={{
              backgroundColor: color.white,
            }}
          />
        )}
      </SidePanelContent>

      <SidePanelFooter
        style={getFooterStyle()}
        preChildren={selectedApprovers.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: '16px 0',
              fontSize: 14,
              fontWeight: 'bold',
              lineHeight: '20px',
            }}
          >
            {selectedApprovers.map((approver) => (
              <div
                key={`selected-approver-${approver._id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 8,
                  backgroundColor: color.lightGray,
                  border: `1px solid ${color.whiteSmoke}`,
                  borderRadius: 4,
                }}
              >
                <VibeIcon
                  style={{
                    marginRight: 8,
                  }}
                  icon={viUser}
                  color={color.primary}
                  size={16}
                />

                {`${approver.fname} ${approver.lname}`.trim()} Selected
              </div>
            ))}
          </div>
        )}
      >
        <VibeButton
          text="Assign"
          style={{
            marginRight: 12,
          }}
          color="secondary"
          // disabled={disableSave || disableInput}
          onClick={onAssignApprovers}
        />

        <VibeButton
          variant="text"
          text="Cancel"
          color="secondary"
          onClick={onClose}
        />
      </SidePanelFooter>
    </SidePanelContainer>
  );
}

export default ApproverList;
